import React, {useEffect, useMemo, useState} from "react";
import {
  Box,
  SimpleGrid,
  Text,
  Input,
  Button,
  Flex,
  useToast,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import {fetchDependentProfile, updateDependentProfile, messageFor} from "../../api/dependents";
import {useDependentSection, SECTION_STATES} from "./useDependentSection";
import DependentShell from "./DependentShell";
import {formatDate, humanizeField, renderValue} from "./format";

// The dependent's profile, exactly as far as the guardian may see it.
//
// Under a restricted grade the sensitive fields are ABSENT from the payload —
// not null, not blank, not masked. So this screen renders what it was given and
// never draws an empty box where a value would be: an empty box confirms the
// clinic holds a value, and for an address in a safety situation that
// confirmation is itself the disclosure.
//
// What it does instead is say plainly that the view is narrowed, so nobody
// phones the clinic to report missing data.

const IDENTITY_FIELDS = [
  ["fName", "First name"],
  ["mName", "Middle name"],
  ["lName", "Last name"],
  ["dob", "Date of birth"],
  ["preferredName", "Preferred name"],
  ["gender", "Gender"],
];

export default function DependentProfile({state}) {
  const toast = useToast();
  const section = useDependentSection({state, section: "profile", load: fetchDependentProfile});

  const profile = section.data?.profile || {};
  const editableFields = useMemo(
    () => (section.data?.editableFields || []).filter((field) => field in profile),
    [section.data, profile]
  );

  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({});
  }, [section.data]);

  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const result = await updateDependentProfile(state, section.dependentPatientId, draft);
      // The server says what it refused. Silently dropping half a submission is
      // how somebody comes to believe they changed something they did not.
      const rejected = result?.rejectedFields || [];
      toast({
        status: rejected.length ? "warning" : "success",
        title: rejected.length ? "Saved, with some fields left unchanged" : "Saved",
        description: rejected.length
          ? `Your clinic does not allow guardians to change: ${rejected
              .map(humanizeField)
              .join(", ")}.`
          : undefined,
        duration: 6000,
        isClosable: true,
      });
      setDraft({});
      section.reload();
    } catch (error) {
      toast({
        status: "error",
        title: "Not saved",
        description: messageFor(error, "Please try again."),
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const shownIdentity = IDENTITY_FIELDS.filter(([key]) => key in profile);
  const restricted = section.data?.fieldScope === "non_sensitive";

  return (
    <DependentShell
      title="Profile"
      subtitle={`${section.displayName}'s details`}
      displayName={section.displayName}
      accessLevel={section.dependent?.accessLevel}
      status={section.status}
      error={section.error}
      onRetry={section.reload}
      onLeave={section.leave}
      actions={
        editableFields.length > 0 &&
        section.status === SECTION_STATES.READY && (
          <Button
            colorScheme="orange"
            size="sm"
            isDisabled={!dirty}
            isLoading={saving}
            onClick={save}
          >
            Save changes
          </Button>
        )
      }
    >
      {section.status === SECTION_STATES.READY && (
        <>
          {restricted && (
            <Alert status="info" borderRadius="10px" mb={5} fontSize="sm">
              <AlertIcon />
              Your clinic has given you a restricted view. You can see who this person is, but not
              their contact details or other personal information.
            </Alert>
          )}

          <Panel heading="Identity">
            <SimpleGrid columns={{base: 1, md: 3}} spacing={4}>
              {shownIdentity.map(([key, label]) => (
                <ReadOnly
                  key={key}
                  label={label}
                  value={key === "dob" ? formatDate(profile[key]) : profile[key]}
                />
              ))}
            </SimpleGrid>
            <Text fontSize="xs" color="gray.500" mt={3}>
              Name and date of birth are kept by your clinic. Contact them if any of this is wrong.
            </Text>
          </Panel>

          {editableFields.length > 0 && (
            <Panel heading="Contact details you can update">
              <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                {editableFields.map((field) => (
                  <FormControl key={field}>
                    <FormLabel fontSize="12px" color="gray.600" mb={1}>
                      {humanizeField(field)}
                    </FormLabel>
                    <Input
                      size="sm"
                      borderRadius="8px"
                      value={draft[field] ?? profile[field] ?? ""}
                      onChange={(event) =>
                        setDraft((current) => ({...current, [field]: event.target.value}))
                      }
                    />
                  </FormControl>
                ))}
              </SimpleGrid>
            </Panel>
          )}

          <OtherDetails profile={profile} editable={editableFields} identity={shownIdentity} />
        </>
      )}
    </DependentShell>
  );
}

/** Whatever else the server sent that this screen has no dedicated place for. */
function OtherDetails({profile, editable, identity}) {
  const placed = new Set([...editable, ...identity.map(([key]) => key), "pid", "avatar"]);
  const rest = Object.keys(profile).filter(
    (key) => !placed.has(key) && profile[key] !== null && profile[key] !== ""
  );
  if (rest.length === 0) return null;
  return (
    <Panel heading="Other details">
      <SimpleGrid columns={{base: 1, md: 3}} spacing={4}>
        {rest.map((key) => (
          <ReadOnly key={key} label={humanizeField(key)} value={renderValue(key, profile[key])} />
        ))}
      </SimpleGrid>
    </Panel>
  );
}

function Panel({heading, children}) {
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={5} bg="white" mb={5}>
      <Text
        fontSize="11px"
        letterSpacing="0.04em"
        textTransform="uppercase"
        color="gray.500"
        mb={4}
      >
        {heading}
      </Text>
      {children}
    </Box>
  );
}

function ReadOnly({label, value}) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Box>
      <Text fontSize="12px" color="gray.600">
        {label}
      </Text>
      <Text fontSize="14px" fontWeight="500" color="#1a1f36">
        {value}
      </Text>
    </Box>
  );
}
